<template>
  <div>
    <SpeedInsights />
    <router-view></router-view>
  </div>
</template>

<script>
import { SpeedInsights } from "@vercel/speed-insights/vue";
import { ElNotification } from 'element-plus';
import { Bell } from '@element-plus/icons-vue';
import { getAccessToken } from '@/utils/token'
import NotificationService from '@/services/notificationService'
import apolloClient from '@/services/apollo-client'

export default {
  name: "App",
  components: {
    SpeedInsights,
  },
  data() {
    return {
      notificationService: null,
    }
  },
  mounted() {
    console.log('=== APP MOUNTED ===');
    const store = this.$store

    // Check if user is logged in but no user data in store or missing pharmacy info
    const token = getAccessToken()
    const user = store.getters['user/user']
    
    console.log('Token exists:', !!token);
    console.log('User from store:', user);
    console.log('User ID:', user?.id);
    
    if (token && (!user?.id || (user?.pharmacy_id && !user?.pharmacy))) {
      console.log('Fetching user data...');
      store.dispatch('user/fetchUser').then(() => {
        // User data loaded successfully
        console.log('User data loaded, initializing notifications...');
        this.initializeNotifications()
      }).catch(error => {
        console.error('App mounted - Error fetching user:', error)
      })
    } else if (token && user?.id) {
      console.log('User already loaded, initializing notifications...');
      this.initializeNotifications()
    } else {
      console.log('No token or user, skipping notification initialization');
    }
  },
  methods: {
    initializeNotifications() {
      console.log('=== INITIALIZING NOTIFICATIONS ===');
      try {
        console.log('Creating NotificationService...');
        // Initialize notification service
        this.notificationService = new NotificationService(apolloClient)
        console.log('NotificationService created:', this.notificationService);
        
        console.log('Requesting browser notification permission...');
        // Request browser notification permission
        this.notificationService.requestPermission()
        
        console.log('Starting notification listening...');
        // Start listening for real-time notifications
        this.notificationService.startListening()
        
        console.log('Adding notification listener...');
        // Initialize notification stats (force fresh fetch from DB on app mount)
        this.$store.dispatch('notifications/initializeNotificationStats', { forceRefresh: true })
        
        // Add listener for new notifications
        this.notificationService.addListener((notification) => {
          console.log('Notification listener triggered:', notification);
          
          // Update notification count in store
          this.$store.dispatch('notifications/incrementUnreadCount')
          
          // Show browser notification
          this.notificationService.showBrowserNotification(notification)
          
          // Get the message
          const message = this.notificationService.getNotificationMessage(notification);
          console.log('Notification message to display:', message);
          
          // Show toast notification
          try {
            // Play notification sound
            this.playNotificationSound();
            
            ElNotification({
              title: this.$t('notifications.new.title'),
              message: message,
              type: 'info',
              duration: 5000,
              position: 'bottom-right',
              icon: Bell,
              customClass: 'notification-info-message'
            });
            console.log('ElNotification displayed successfully');
          } catch (error) {
            console.error('Error showing ElNotification:', error);
            // Fallback to alert
            alert(`New notification: ${message}`);
          }
        })
        
        console.log('Setting up global test methods...');
        // Make notification service available globally for testing
        window.notificationService = this.notificationService
        console.log('Notification service initialized and available as window.notificationService')
        
        // Add a test method to window for easy debugging
        window.testNotification = () => {
          if (this.notificationService) {
            this.notificationService.testNotification()
          } else {
            console.error('Notification service not available')
          }
        }
        
        // Add a method to clear notification cache for testing
        window.clearNotificationCache = () => {
          if (this.notificationService) {
            this.notificationService.clearProcessedCache()
          } else {
            console.error('Notification service not available')
          }
        }
        
        // Add a method to clear old test notifications
        window.clearOldTestNotifications = () => {
          if (this.notificationService) {
            this.notificationService.clearOldTestNotifications()
          } else {
            console.error('Notification service not available')
          }
        }
        
        console.log('Test notification available as window.testNotification()')
        console.log('=== NOTIFICATIONS INITIALIZED SUCCESSFULLY ===');
      } catch (error) {
        console.error('Failed to initialize notifications:', error)
        console.error('Error stack:', error.stack);
      }
    },
    
    playNotificationSound() {
      try {
        // Create audio context for notification sound
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        // Connect nodes
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        // Configure sound
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // 800Hz tone
        oscillator.type = 'sine';
        
        // Configure volume
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime); // 30% volume
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        
        // Play sound
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3); // 300ms duration
        
        console.log('Notification sound played');
      } catch (error) {
        console.error('Error playing notification sound:', error);
        // Fallback: try to play a simple beep using HTML5 Audio
        try {
          const audio = new Audio();
          audio.volume = 0.3;
          // Create a simple beep using data URL
          audio.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT';
          audio.play().catch(e => console.log('Audio fallback failed:', e));
        } catch (fallbackError) {
          console.error('Fallback audio also failed:', fallbackError);
        }
      }
    }
  },
  beforeUnmount() {
    if (this.notificationService) {
      this.notificationService.stopListening()
    }
  }
};
</script>

<style>
/* Custom notification message styling for info notifications - Maximum specificity */
.notification-info-message .el-notification__icon,
.notification-info-message .el-notification__icon i,
.notification-info-message .el-notification__icon .fas,
.notification-info-message .el-notification__icon .fa-bell {
  color: #3b82f6 !important; /* Blue color */
  font-size: 20px !important;
}

:deep(.notification-info-message .el-notification__icon) {
  color: #3b82f6 !important; /* Blue color */
  font-size: 20px !important;
}

:deep(.notification-info-message .el-notification__icon i) {
  color: #3b82f6 !important; /* Blue color for any icon */
}

:deep(.notification-info-message .el-notification__icon .fas.fa-bell) {
  color: #3b82f6 !important; /* Blue color for bell icon */
}

/* Global override for all notification info messages */
.el-notification.notification-info-message .el-notification__icon {
  color: #3b82f6 !important;
  font-size: 20px !important;
}
</style>
